import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Calendar, Users, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSchedules = () => {
  const { token } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    bundle_id: '',
    modality: 'ead_live',
    class_name: '',
    start_date: '',
    end_date: '',
    max_students: 25,
    instructor: 'Alexander Justi',
    color: '#dc2626',
    status: 'open'
  });
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, bundlesRes] = await Promise.all([
        axios.get(`${API}/schedules`),
        axios.get(`${API}/bundles`)
      ]);
      setSchedules(schedulesRes.data);
      setBundles(bundlesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (sessions.length === 0) {
      toast.error('Adicione pelo menos uma aula');
      return;
    }

    try {
      const payload = {
        ...formData,
        bundle_id: parseInt(formData.bundle_id),
        max_students: parseInt(formData.max_students),
        enrolled_count: 0,
        schedule: sessions
      };

      if (editingSchedule) {
        await axios.put(`${API}/schedules/${editingSchedule.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Turma atualizada com sucesso!');
      } else {
        await axios.post(`${API}/schedules`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Turma criada com sucesso!');
      }

      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar turma');
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      bundle_id: schedule.bundle_id,
      modality: schedule.modality,
      class_name: schedule.class_name,
      start_date: schedule.start_date.split('T')[0],
      end_date: schedule.end_date.split('T')[0],
      max_students: schedule.max_students,
      instructor: schedule.instructor,
      color: schedule.color,
      status: schedule.status
    });
    setSessions(schedule.schedule);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta turma?')) return;

    try {
      await axios.delete(`${API}/schedules/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Turma excluída com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir turma');
    }
  };

  const resetForm = () => {
    setFormData({
      bundle_id: '',
      modality: 'ead_live',
      class_name: '',
      start_date: '',
      end_date: '',
      max_students: 25,
      instructor: 'Alexander Justi',
      color: '#dc2626',
      status: 'open'
    });
    setSessions([]);
    setEditingSchedule(null);
    setShowForm(false);
  };

  const addSession = () => {
    setSessions([...sessions, {
      date: new Date().toISOString().split('T')[0],
      start_time: '19:00',
      end_time: '22:00',
      topic: '',
      status: 'scheduled'
    }]);
  };

  const updateSession = (index, field, value) => {
    const updated = [...sessions];
    updated[index][field] = value;
    setSessions(updated);
  };

  const removeSession = (index) => {
    setSessions(sessions.filter((_, i) => i !== index));
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-green-600',
      full: 'bg-red-600',
      in_progress: 'bg-blue-600',
      completed: 'bg-gray-600'
    };
    return colors[status] || 'bg-gray-600';
  };

  if (loading) {
    return <div className="text-white">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Gerenciar Turmas</h2>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-red-600 hover:bg-red-700"
        >
          {showForm ? <X size={20} /> : <Plus size={20} />}
          <span className="ml-2">{showForm ? 'Cancelar' : 'Nova Turma'}</span>
        </Button>
      </div>

      {/* Formulário */}
      {showForm && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">
              {editingSchedule ? 'Editar Turma' : 'Nova Turma'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Formação</Label>
                  <select
                    value={formData.bundle_id}
                    onChange={(e) => setFormData({ ...formData, bundle_id: e.target.value })}
                    className="w-full bg-zinc-800 text-white border-zinc-700 rounded-md p-2"
                    required
                  >
                    <option value="">Selecione...</option>
                    {bundles.map(bundle => (
                      <option key={bundle.id} value={bundle.id}>{bundle.title}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label className="text-white">Modalidade</Label>
                  <select
                    value={formData.modality}
                    onChange={(e) => setFormData({ ...formData, modality: e.target.value })}
                    className="w-full bg-zinc-800 text-white border-zinc-700 rounded-md p-2"
                    required
                  >
                    <option value="ead_live">Elite (Ao Vivo)</option>
                    <option value="presential">Mentor (Presencial)</option>
                  </select>
                </div>

                <div>
                  <Label className="text-white">Nome da Turma</Label>
                  <Input
                    value={formData.class_name}
                    onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
                    className="bg-zinc-800 text-white border-zinc-700"
                    placeholder="Ex: Turma Elite - Janeiro 2025"
                    required
                  />
                </div>

                <div>
                  <Label className="text-white">Instrutor</Label>
                  <Input
                    value={formData.instructor}
                    onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                    className="bg-zinc-800 text-white border-zinc-700"
                    required
                  />
                </div>

                <div>
                  <Label className="text-white">Data Início</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="bg-zinc-800 text-white border-zinc-700"
                    required
                  />
                </div>

                <div>
                  <Label className="text-white">Data Término</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="bg-zinc-800 text-white border-zinc-700"
                    required
                  />
                </div>

                <div>
                  <Label className="text-white">Máx. Alunos</Label>
                  <Input
                    type="number"
                    value={formData.max_students}
                    onChange={(e) => setFormData({ ...formData, max_students: e.target.value })}
                    className="bg-zinc-800 text-white border-zinc-700"
                    min="1"
                    required
                  />
                </div>

                <div>
                  <Label className="text-white">Status</Label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full bg-zinc-800 text-white border-zinc-700 rounded-md p-2"
                  >
                    <option value="open">Aberta</option>
                    <option value="full">Cheia</option>
                    <option value="in_progress">Em Andamento</option>
                    <option value="completed">Concluída</option>
                  </select>
                </div>

                <div>
                  <Label className="text-white">Cor</Label>
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 h-10"
                  />
                </div>
              </div>

              {/* Aulas */}
              <div className="border-t border-zinc-700 pt-4">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-white text-lg">Aulas ({sessions.length})</Label>
                  <Button type="button" onClick={addSession} size="sm" className="bg-green-600 hover:bg-green-700">
                    <Plus size={16} className="mr-1" /> Adicionar Aula
                  </Button>
                </div>

                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {sessions.map((session, index) => (
                    <div key={index} className="bg-zinc-800 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-semibold">Aula {index + 1}</span>
                        <Button
                          type="button"
                          onClick={() => removeSession(index)}
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-4 gap-3">
                        <Input
                          type="date"
                          value={session.date.split('T')[0]}
                          onChange={(e) => updateSession(index, 'date', e.target.value)}
                          className="bg-zinc-900 text-white border-zinc-700"
                        />
                        <Input
                          type="time"
                          value={session.start_time}
                          onChange={(e) => updateSession(index, 'start_time', e.target.value)}
                          className="bg-zinc-900 text-white border-zinc-700"
                        />
                        <Input
                          type="time"
                          value={session.end_time}
                          onChange={(e) => updateSession(index, 'end_time', e.target.value)}
                          className="bg-zinc-900 text-white border-zinc-700"
                        />
                        <Input
                          value={session.topic}
                          onChange={(e) => updateSession(index, 'topic', e.target.value)}
                          placeholder="Tópico da aula"
                          className="bg-zinc-900 text-white border-zinc-700"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3">
                <Button type="submit" className="bg-red-600 hover:bg-red-700">
                  {editingSchedule ? 'Atualizar' : 'Criar'} Turma
                </Button>
                <Button type="button" onClick={resetForm} variant="outline" className="border-zinc-700 text-white">
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Lista de Turmas */}
      <div className="grid md:grid-cols-2 gap-6">
        {schedules.map(schedule => (
          <Card key={schedule.id} className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-white text-lg">{schedule.class_name}</CardTitle>
                  <p className="text-sm text-gray-400 mt-1">
                    {bundles.find(b => b.id === schedule.bundle_id)?.title}
                  </p>
                </div>
                <Badge className={`${getStatusColor(schedule.status)} text-white`}>
                  {schedule.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400">Modalidade:</span>
                  <p className="text-white">{schedule.modality === 'ead_live' ? 'Elite' : 'Mentor'}</p>
                </div>
                <div>
                  <span className="text-gray-400">Instrutor:</span>
                  <p className="text-white">{schedule.instructor}</p>
                </div>
                <div>
                  <span className="text-gray-400">Vagas:</span>
                  <p className="text-white">{schedule.enrolled_count}/{schedule.max_students}</p>
                </div>
                <div>
                  <span className="text-gray-400">Aulas:</span>
                  <p className="text-white">{schedule.schedule.length}</p>
                </div>
              </div>

              <div className="flex space-x-2">
                <Button
                  onClick={() => handleEdit(schedule)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 flex-1"
                >
                  <Edit size={16} className="mr-1" /> Editar
                </Button>
                <Button
                  onClick={() => handleDelete(schedule.id)}
                  size="sm"
                  variant="destructive"
                  className="flex-1"
                >
                  <Trash2 size={16} className="mr-1" /> Excluir
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminSchedules;